import React, { forwardRef } from 'react'
import { styled, useTheme } from '@mui/material/styles'
import { Typography } from '@mui/material'

// Direct styling (Recommended - Simplest)
const StyledTypography = styled(Typography)(() => ({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

// Using forwardRef (When you need custom component logic)
const StyledTypographyWithRef = styled(forwardRef(({ ...props }, ref) => <Typography ref={ref} {...props} />))(() => ({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

// Reusable component with forwardRef (Most scalable)
const EllipsisTypography = forwardRef(({ lines = 2, ...props }, ref) => <Typography ref={ref} {...props} />)

const StyledEllipsisTypography = styled(EllipsisTypography, {
  shouldForwardProp: (prop) => prop !== 'lines', // Impede que 'lines' vá para o HTML
})(({ lines = 2 }) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

// Using shouldForwardProp for advanced control
const AdvancedStyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => !['lines', 'customProp'].includes(prop),
})(({ lines = 2 }) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

const useEllipsisStyles = (lines = 2) => {
  const theme = useTheme()

  return {
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    // You can add theme-based styles here
    fontSize: theme.typography.body2.fontSize,
  }
}

// Usage with custom hook
function EllipsisTypographyWithHook({ lines = 2, sx, ...props }) {
  const ellipsisStyles = useEllipsisStyles(lines)

  return (
    <Typography
      sx={{
        ...ellipsisStyles,
        ...sx, // Allow style overrides
      }}
      {...props}
    />
  )
}

// Component with displayName (Better debugging)
const ClampedTypography = styled(Typography, {
  name: 'ClampedTypography',
  slot: 'Root',
})(({ theme, lines = 2 }) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // Example of using theme
  color: theme.palette.text.secondary,
}))

ClampedTypography.displayName = 'ClampedTypography'

// Factory function for different variants
const createEllipsisTypography = (defaultLines = 2) =>
  styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'lines',
  })(({ lines = defaultLines }) => ({
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }))

// Create specific variants
const TwoLineTypography = createEllipsisTypography(2)
const ThreeLineTypography = createEllipsisTypography(3)

// With TypeScript support (if using TypeScript on brabus DS)
// interface EllipsisTypographyProps {
//   lines?: number
//   children: React.ReactNode
//   // Add other Typography props as needed
// }

// const TypedEllipsisTypography =
//   styled(Typography) <
//   EllipsisTypographyProps >
//   (({ lines = 2 }) => ({
//     display: '-webkit-box',
//     WebkitLineClamp: lines,
//     WebkitBoxOrient: 'vertical',
//     overflow: 'hidden',
//     textOverflow: 'ellipsis',
//   }))

// Usage examples:
/**
 * Usage examples (to the Storybook)
 * export function ExampleUsage() {
 * <StyledTypography>This text will be clamped to 2 lines with ellipsis overflow handling</StyledTypography>
 * <StyledEllipsisTypography lines={3}>
 *   This text will be clamped to 3 lines with ellipsis overflow handling
 * </StyledEllipsisTypography>
 * <AdvancedStyledTypography lines={1}>This text will be clamped to 1 line</AdvancedStyledTypography>
 * <EllipsisTypographyWithHook lines={2}>
 *   Using custom hook approach for maximum flexibility
 * </EllipsisTypographyWithHook>
 * <TwoLineTypography>Two line variant</TwoLineTypography>
 * <ThreeLineTypography>Three line variant</ThreeLineTypography>
 */

export {
  StyledTypography,
  StyledTypographyWithRef,
  StyledEllipsisTypography,
  AdvancedStyledTypography,
  EllipsisTypographyWithHook,
  ClampedTypography,
  TwoLineTypography,
  ThreeLineTypography,
}
